import * as React from 'react';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import type { UserConnecture } from '../../MedicareProAPI/Entities/User/UserConnecture';
import { IndAppAlternateIdRepository } from '../../MedicareProAPI/Repositories/Ind/IndAppAlternateIdRepository';
import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import StyledModal from '../Components/StyledModal';

export interface ConnectureEnrollButtonProps {
  userId: number;
  userAccessToken: string;
  indId: number;
  thisUserConnecture: UserConnecture[];
  stack?: boolean;
}

type Status =
  | 'idle'
  | 'checkingSession'
  | 'choice'
  | 'login'
  | 'makingSAML'
  | 'makingSession'
  | 'done'
  | 'error';

/**
 * Modal that orchestrates Connecture (PCEA) session flow and prospect post.
 *
 * @remarks
 * Workflow:
 * 1) Fetch existing session value via {@link fetchExistingSSOValue}.
 * 2) If found, prompt user to resume or start a new session (see {@link handleSessionChoice}).
 * 3) On choice, route through Auth0 login ({@link handleLogin}).
 * 4) Receive SAML response ({@link onMessage}).
 * 5) Build internal SAML payload ({@link makeIndSAML}).
 * 6) Create/refresh session if permitted ({@link makeSession}).
 * 7) Show CTA to continue to Connecture (CNX).
 *
 * @version 1.1.0
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @public
 */
export const ConnectureEnrollButton = (props: ConnectureEnrollButtonProps): React.JSX.Element => {
  const userRepository = React.useMemo(() => {
    const r = new UserRepository();
    r.setAccessToken(props.userAccessToken);
    return r;
  }, [props.userAccessToken]);

  const indAppAlternateIdRepository = React.useMemo(() => {
    const r = new IndAppAlternateIdRepository();
    r.setAccessToken(props.userAccessToken);
    return r;
  }, [props.userAccessToken]);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const SSO_LOGIN_URL = 'api/v1/auth0/saml/connecture/main/login';

  const [modalKey, setModalKey] = React.useState(0);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);

  const [userConnecture, setUserConnecture] = React.useState<Array<UserConnecture>>(
    props.thisUserConnecture
  );
  const [userConnectureSelected, setUserConnectureSelected] = React.useState<UserConnecture>();

  const [sessionId, setSessionId] = React.useState<string>('');
  const [useExistingSession, setUseExistingSession] = React.useState<boolean>(false);
  const [sessionResponse, setSessionResponse] = React.useState<any>();

  const [auth0Response, setAuth0response] = React.useState<string>();
  const [samlResponse, setSamlResponse] = React.useState<string>();

  const [prospectPostUrl, setProspectPostUrl] = React.useState<string>('');

  const [status, setStatus] = React.useState<Status>('idle');
  const [error, setError] = React.useState<string | null>(null);

  const [showIntegrationInfo, setShowIntegrationInfo] = React.useState<boolean>(false);
  const [showChooseInfo, setShowChooseInfo] = React.useState<boolean>(false);

  //const [partnerId, setPartnerId] = React.useState<string>();

  const cantMakeSessionOrg = new Set(['pcea']);

  const ALL_CAN_POST_OPTIONS = [
    'Name',
    'DOB',
    'Email',
    'Phone',
    'Address',
    'MBI',
    'Medicaid',
    'Rx',
    'Pharmacies',
    'Providers',
  ];

  const FULL_POST = new Set([
    'Name',
    'DOB',
    'Email',
    'Phone',
    'Address',
    'MBI',
    'Medicaid',
    'Rx',
    'Pharmacies',
  ]);

  const PARTIAL_POST = new Set(['Name', 'DOB', 'Email', 'Phone', 'Address', 'MBI', 'Medicaid']);

  const FULL_INGEST = ['Policy', 'Rx', 'Pharmacies', 'Providers'];

  const PARTIAL_INGEST = ['Policy'];

  const partialOrgs = new Set(['pcea']);

  const selectedOrgName = React.useMemo(() => {
    return (
      userConnectureSelected?.getAgencyConnecture()?.getOrgName() ??
      userConnectureSelected?.getGlobConnectureOrg()?.getOrgName() ??
      null
    );
  }, [userConnectureSelected]);

  const selectedOrgReference = React.useMemo(() => {
    return (
      userConnectureSelected?.getAgencyConnecture()?.getReference() ??
      userConnectureSelected?.getGlobConnectureOrg()?.getReference() ??
      null
    );
  }, [userConnectureSelected]);

  const selectedOrgPartnerId = React.useMemo(() => {
    return (
      userConnectureSelected?.getAgencyConnecture()?.getPartnerId() ??
      userConnectureSelected?.getGlobConnectureOrg()?.getPartnerId() ??
      null
    );
  }, [userConnectureSelected]);

  const relayUrl = React.useMemo(() => {
    return (
      userConnectureSelected?.getAgencyConnecture()?.getUrlRelay() ??
      userConnectureSelected?.getGlobConnectureOrg()?.getUrlRelay() ??
      null
    );
  }, [userConnectureSelected]);

  const samlUrl = React.useMemo(() => {
    return (
      userConnectureSelected?.getAgencyConnecture()?.getUrl() ??
      userConnectureSelected?.getGlobConnectureOrg()?.getUrl() ??
      null
    );
  }, [userConnectureSelected]);

  const canPostSet = new Set([]);

  /**
   * Resets all Connecture enrollment/session UI state back to initial values.
   *
   * Side effects:
   * - Clears the selected Connecture account (`setUserConnectureSelected(undefined)`).
   * - Empties the current session id (`setSessionId('')`).
   * - Sets status back to `'idle'`.
   * - Clears any error (`setError(null)`).
   * - Clears the last session response (`setSessionResponse(null)`).
   * - Clears any generated prospect post URL (`setProspectPostUrl('')`).
   * - Disables the "use existing session" flag (`setUseExistingSession(false)`).
   *
   * @returns {void}
   * @example
   * // After closing the modal:
   * handleClose = () => {
   *   setModalOpen(false);
   *   reset();
   * };
   */
  const reset = React.useCallback((): void => {
    setUserConnectureSelected(undefined);
    setSessionId('');
    setStatus('idle');
    setError(null);
    setSessionResponse(null);
    setProspectPostUrl('');
    setUseExistingSession(false);
  }, []);

  /**
   * Opens the enrollment modal. If exactly one Connecture account exists,
   * preselect it for the user.
   *
   * @returns {void}
   *
   * @example
   * // On button click
   * <button onClick={handleStartEnrollClick}>Start Enrollment</button>
   */
  const handleStartEnrollClick = () => {
    setModalOpen(true);

    if (userConnecture?.length === 1) {
      console.log('connecture selected:', userConnecture[0]);
      setUserConnectureSelected(userConnecture[0]);
    }
  };

  React.useEffect(() => {
    if (!userConnectureSelected) return;
    console.log('selected org snapshot:', {
      agency: userConnectureSelected.getAgencyConnecture?.() && {
        ref: userConnectureSelected.getAgencyConnecture().getReference?.(),
        partner: userConnectureSelected.getAgencyConnecture().getPartnerId?.(),
        name: userConnectureSelected.getAgencyConnecture().getOrgName?.(),
      },
      glob: userConnectureSelected.getGlobConnectureOrg?.() && {
        ref: userConnectureSelected.getGlobConnectureOrg().getReference?.(),
        partner: userConnectureSelected.getGlobConnectureOrg().getPartnerId?.(),
        name: userConnectureSelected.getGlobConnectureOrg().getOrgName?.(),
      },
    });
  }, [userConnectureSelected]);

  /**
   * Closes the modal, resets related state, and increments the modal key
   * to force an unmount/mount cycle (useful for clearing internal component state).
   *
   * Side effects:
   * - Calls `setModalOpen(false)` to hide the modal.
   * - Invokes `reset()` to clear enrollment/session state.
   * - Bumps `modalKey` via `setModalKey(k => k + 1)` to remount the modal subtree.
   *
   * @returns {void}
   *
   */
  const handleClose = () => {
    setModalOpen(false);
    reset();
    setModalKey((k) => k + 1);
  };

  /**
   * Fetches the most recent Connecture SSO value (alternate ID) for the individual.
   *
   * Looks up the latest `connecture` app-alternate-id for the given user/individual.
   * If found, sets the session id and moves UI into the "choice" state (resume/new).
   * If none exists, it initiates the login flow.
   *
   * @remarks
   * Side effects:
   * - Calls `setStatus('checkingSession' | 'choice')`
   * - Calls `setSessionId(contactId)` when a record exists
   * - Calls `handleLogin()` when no record exists
   *
   *
   * @throws Propagates repository/network errors to the caller.
   * @returns {Promise<void>} Resolves when the state has been updated / login triggered.
   */
  const fetchExistingSSOValue = async () => {
    setStatus('checkingSession');

    const raw = await indAppAlternateIdRepository.getIndAppAlternateIds(props.userId, props.indId, {
      criteria: { app: 'connecture' },
      order: 'DESC',
      sortBy: 'id',
    });

    if (raw?.total > 0 && raw?.data) {
      setSessionId(raw.data[0].contactId);
      setStatus('choice');
    } else {
      handleLogin();
    }
  };

  /**
   * Chooses whether to reuse an existing Connecture session or start fresh,
   * then proceeds to the login/auth flow.
   *
   * @param useExisting - If `true`, reuse the previously found session; otherwise start a new one.
   *
   * @remarks
   * Preconditions:
   * - `userConnectureSelected` must be set (indicates the selected org supports sessions).
   *
   * Side effects:
   * - Clears any prior error via `setError(null)`.
   * - Persists the choice via `setUseExistingSession(useExisting)`.
   * - Kicks off the auth/SSO path via `handleLogin()`.
   *
   * @returns Promise<void>
   */
  const chooseExisting = async (useExisting: boolean) => {
    // If we are here, the org already support session
    if (!userConnectureSelected) return;
    setError(null);
    setUseExistingSession(useExisting);

    handleLogin();
  };

  /**
   * Initiates the Auth0/SSO login flow in a new tab/window.
   *
   * Sets UI status to `'login'`, derives the SSO URL from the current hostname, and
   * opens it with `noopener,noreferrer` for security. Uses **HTTPS** when the hostname
   * includes `medicareproapp` (prod/staging), otherwise **HTTP** (local dev).
   *
   *
   * @returns {void}
   */
  /*const handleLogin = () => {
        setStatus('login');
        const host = window.location.hostname;
        const protocol = host.indexOf('medicareproapp') >= 0 ? 'https' : 'http';
        const url = `${protocol}://${host}/${SSO_LOGIN_URL}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };*/
  const handleLogin = (): void => {
    setStatus('login');

    const host = window.location.hostname;
    const protocol = host.indexOf('medicareproapp') >= 0 ? 'https' : 'http';
    const url = `${protocol}://${host}/${SSO_LOGIN_URL}`;

    // Keep an opener (no noopener/noreferrer) so callback can postMessage back.
    // Use a named window + reasonable size.
    const win = window.open(
      url,
      'auth0-login',
      'width=720,height=840,menubar=no,toolbar=no,location=no,status=no'
    );

    if (!win) {
      // Optional: surface this in your UI instead of alert
      alert('Your browser blocked the sign-in window. Please allow popups and try again.');
    }
  };

  const makeSession = React.useCallback(async (): Promise<void> => {
    const reference = selectedOrgReference;
    const partnerId = selectedOrgPartnerId;

    console.log('makeSession → reference:', reference, 'partnerId:', partnerId);

    setStatus('makingSession');

    if (reference && cantMakeSessionOrg.has(reference)) {
      setStatus('done');
      return;
    }
    if (!partnerId) {
      setStatus('error');
      setError('Missing Connecture partner ID for selected org.');
      return;
    }

    const params = {
      partnerId,
      indId: props.indId,
      ssoValue: sessionId && useExistingSession ? sessionId : props.indId,
    };

    console.log('makeSession params:', params);

    const session = await webAPI.createConnectureSession(props.userId, params);
    setSessionResponse(session);
    setStatus('done');
  }, [
    selectedOrgPartnerId,
    selectedOrgReference,
    sessionId,
    useExistingSession,
    props.indId,
    props.userId,
  ]);

  /**
   * Builds the individual SAML payload from the Auth0 postMessage and
   * (optionally) includes an existing Connecture SSO value, then proceeds
   * to create/refresh the Connecture session.
   *
   * @returns Promise<void>
   */
  const makeIndSAML = React.useCallback(
    async (saml: any) => {
      console.log('saml passed:', saml);
      setStatus('makingSAML');
      try {
        const payload: any = { SAMLResponse: saml };
        if (sessionId && useExistingSession) payload.existingSSOValue = sessionId;

        console.log('payload:', payload);
        const resp = await userRepository.getAuth0SAMLIndPayload(props.indId, payload);
        setSamlResponse(resp?.SAMLResponse || '');

        await makeSession();
      } catch (err: any) {
        setStatus('error');
        setError(err?.message || 'Unable to prepare SSO payload.');
      }
    },
    [sessionId, useExistingSession, props.indId, userRepository, makeSession]
  );

  /**
   * Subscribes to `window.postMessage` events to receive the Auth0/SSO SAML payload.
   *
   * When a message arrives containing a `SAMLResponse`, this effect:
   * 1) Stores the raw Auth0 response via `setAuth0response`.
   * 2) Triggers `makeIndSAML()` to continue the flow.
   *
   * @remarks
   * Security TODO:
   * - Always validate `event.origin` against known trusted origins to prevent
   *   processing messages from untrusted sources.
   * - Avoid trusting the entire `event.data`; only read the fields you need.
   *
   */
  React.useEffect(() => {
    const onMessage = (e: MessageEvent<any>) => {
      if (!e?.data?.SAMLResponse) return;
      // optionally check origin here
      setAuth0response(e.data.SAMLResponse);
      void makeIndSAML(e.data.SAMLResponse);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [makeIndSAML]);

  /**
   * Starts the Connecture enrollment flow:
   * - Opens modal and clears errors.
   * - If the org supports sessions, tries to reuse an existing one; otherwise creates a new session.
   * - Creates a prospect post and updates status through each phase.
   *
   * Status flow: 'checking' → ('choice' | 'makingSession' → 'posting') → 'done' | 'error'
   *
   * @returns {Promise<void>}
   */
  const startEnroll = async () => {
    if (!userConnectureSelected) return;
    setModalOpen(true);
    setError(null);

    fetchExistingSSOValue();
  };

  /**
   * When the modal opens with a selected Connecture account, kick off enrollment.
   *
   * Triggers:
   * - Runs whenever `modalOpen` or `userConnectureSelected` changes.
   * - If the modal isn’t open or no account is selected, it exits early.
   *
   * Side effects:
   * - Invokes `startEnroll()` which manages status and posting flow.
   */
  React.useEffect(() => {
    if (!modalOpen) return;
    if (!userConnectureSelected) return;
    if (!selectedOrgPartnerId) return; //
    startEnroll();
  }, [modalOpen, userConnectureSelected, selectedOrgPartnerId]);

  return (
    <>
      {userConnecture && !props.stack && (
        <div
          className="btn btn-sm btn-white mb-3 mr-2 float-right"
          onClick={handleStartEnrollClick}
        >
          <img
            style={{ height: '20px' }}
            className="mr-1"
            alt="Connecture"
            src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/connectureicon.png"
          />
          <b>Enroll</b>
        </div>
      )}
      {userConnecture && props.stack && (
        <div
          className={`btn border-orange text-orange btn-sm p-2 mt-2 d-block mx-auto cp px-3 hover-shadow `}
          style={{ borderRadius: '15px' }}
          onClick={handleStartEnrollClick}
        >
          <img
            className="mr-2"
            style={{ height: '20px' }}
            alt="Connecture"
            src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/connectureicon.png"
          />
          <b>Connecture</b>
        </div>
      )}
      {modalOpen && (
        <StyledModal
          key={modalKey}
          size="lg"
          title="Connecture"
          footerCloseButton
          show={true}
          onClose={handleClose}
        >
          {userConnecture.length > 1 && !userConnectureSelected && (
            <div className="row">
              <div className="col-12">
                <div className="mb-2">
                  <b>Choose Connecture Org</b>
                </div>
                {userConnecture.map((item, idx) => (
                  <div
                    className="rounded bg-contrast p-2 mb-2 cp"
                    onClick={() => setUserConnectureSelected(item)}
                  >
                    <img
                      style={{ height: '35px' }}
                      alt="Sunfire Matrix"
                      src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/connectureicon.png"
                    />
                    <span className="ml-2">
                      <b>
                        {item.getAgencyConnecture()
                          ? item.getAgencyConnecture().getOrgName()
                          : item.getGlobConnectureOrg().getOrgName()}
                      </b>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {userConnectureSelected && (
            <div className="row">
              {/* left column */}
              <div className="col-12 col-lg-8 text-center align-self-center">
                {status === 'checkingSession' && (
                  <div>
                    <b>
                      <i className="far fa-spinner text-orange fa-spin" /> Checking for existing
                      session…
                    </b>
                  </div>
                )}

                {status === 'choice' && (
                  <>
                    <div>
                      <i className="fas fa-check-circle text-orange" />{' '}
                      <b>Existing session found</b>
                    </div>

                    <div
                      className={`btn border-orange text-orange btn-sm p-2 mt-2 cp mt-5 px-3 hover-shadow `}
                      onClick={() => chooseExisting(true)}
                      style={{ borderRadius: '15px' }}
                    >
                      <b>
                        Continue with session <i className="far fa-arrow-alt-circle-right ml-2"></i>
                      </b>{' '}
                      <span className="dataBadge"></span>
                    </div>

                    <div className="mb-2 mt-2">
                      <b>OR</b>
                    </div>
                    <div className="mb-2 mt-2">
                      <div
                        className={`btn border-orange text-orange btn-sm p-2 d-inline-block cp px-3 hover-shadow `}
                        onClick={() => chooseExisting(false)}
                        style={{ borderRadius: '15px' }}
                      >
                        <b>
                          Start new session <i className="far fa-arrow-alt-circle-right ml-2"></i>
                        </b>
                      </div>
                    </div>
                    {!showChooseInfo && (
                      <div className="mt-5 text-left">
                        <div
                          className="badge border-secondary cp"
                          onClick={() => setShowChooseInfo(!showChooseInfo)}
                        >
                          <i className="fas fa-question-circle mr-1"></i>Help me decide
                        </div>
                      </div>
                    )}

                    {showChooseInfo && (
                      <div className="rounded p-3 bg-contrast small p-2 mt-5 text-left">
                        <i className="fas fa-info-circle mr-1" />
                        <b>Choose how to proceed:</b>
                        <ul className="mb-0 mt-2 pl-3">
                          <li>
                            <b>Use existing session</b> — MPCRM will <b>replace</b> the data on this
                            Connecture profile with what it sends now (keeps the current
                            profile/session ID).
                          </li>
                          <li className="mt-1">
                            <b>Start new session</b> — creates a <b>new Connecture profile</b>.
                          </li>
                        </ul>
                        <div
                          className="badge badge-secondary cp mt-2"
                          onClick={() => setShowChooseInfo(!showChooseInfo)}
                        >
                          Close
                        </div>
                      </div>
                    )}
                  </>
                )}

                {status === 'login' && (
                  <>
                    <div>
                      <b>
                        <i className="far fa-spinner text-orange fa-spin" /> Establishing SSO…
                      </b>
                    </div>
                    <div className="mt-3 rounded bg-contrast p-3 small text-left">
                      <i className="far fa-info-circle text-orange mr-1"></i>You should be
                      redirected to an MPCRM login screen to establish the SSO connection.
                    </div>
                  </>
                )}

                {status === 'makingSAML' && (
                  <div>
                    <b>
                      <i className="far fa-spinner text-orange fa-spin" /> Preparing SAML...
                    </b>
                  </div>
                )}

                {status === 'makingSession' && (
                  <div>
                    <b>
                      <i className="far fa-spinner text-orange fa-spin" /> Creating session…
                    </b>
                  </div>
                )}

                {status == 'done' && (
                  <>
                    <form target="_blank" action={samlUrl} method="POST">
                      <textarea name="SAMLResponse">{samlResponse}</textarea>
                      <textarea name="RelayState">{relayUrl}</textarea>
                      <button
                        className={`btn border-orange text-orange btn-sm p-2 d-inline-block cp px-3 hover-shadow ${!prospectPostUrl ? 'disabled' : ''}`}
                        //href={prospectPostUrl ?? '#'}
                        style={{ borderRadius: '15px', background: 'none' }}
                        type="submit"
                        // target="_blank"
                        //rel="noopener noreferrer"
                        // onClick={(e) => { if (!prospectPostUrl) e.preventDefault(); }}
                        disabled={!samlUrl}
                      >
                        <img
                          className="mr-2"
                          style={{ height: '20px' }}
                          alt="Connecture"
                          src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/connectureicon.png"
                        />
                        <b>Continue to Connecture</b>{' '}
                        <i className="far fa-arrow-alt-circle-right ml-2"></i>
                      </button>
                    </form>

                    {!sessionId && (
                      <div className="mt-3 rounded bg-contrast p-3 small text-left">
                        <i className="far fa-info-circle text-orange mr-1"></i>If you think this
                        profile may already exists on Connecture, pressing continue will create a
                        new profile (possible duplicate on Connecture).
                      </div>
                    )}
                  </>
                )}

                {status === 'error' && (
                  <div className="mt-2 rounded bg-contrast p-3 small text-left">
                    <div className="text-danger mb-2" aria-hidden="true">
                      <b>
                        <i className="far fa-exclamation-circle mr-2"></i>We couldn’t send data to
                        Connecture.
                      </b>
                    </div>
                    {<>{error}</>}
                  </div>
                )}
              </div>

              {/* right column (sibling, not nested inside col-8) */}
              <div className="col-12 col-lg-4">
                {selectedOrgName && (
                  <>
                    <div className="mb-2">
                      What we can send to Connecture through <b>{selectedOrgName}</b>
                    </div>
                    {ALL_CAN_POST_OPTIONS.map((opt) => {
                      const allowed = partialOrgs.has(selectedOrgReference)
                        ? PARTIAL_POST.has(opt)
                        : FULL_POST.has(opt);
                      return (
                        <span
                          key={opt}
                          className={`badge dataBadge text-dark mr-2 cp ${allowed ? 'table-success' : 'table-danger'}`}
                          title={allowed ? 'Included' : 'Not included'}
                          onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                        >
                          <i className={`mr-1 far ${allowed ? 'fa-check' : 'fa-times'}`}></i>
                          {opt}
                        </span>
                      );
                    })}

                    <div className="mb-2 mt-4">
                      What Connecture <b>sends back</b> after an <b>enrollment</b>
                    </div>
                    {(partialOrgs.has(selectedOrgReference)
                      ? PARTIAL_INGEST
                      : FULL_INGEST || []
                    ).map((item, idx) => (
                      <span
                        key={idx}
                        className="badge table-success dataBadge text-dark mr-2 cp"
                        onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                      >
                        <i className={`mr-1 far fa-check`}></i>
                        {item}
                      </span>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </StyledModal>
      )}
    </>
  );
};
